/**
 * Personas (cibles) — target-audience cards, each framing the challenge (pains)
 * against what Karexia delivers (gains). Content lives in landing-data.ts.
 */

import { PERSONAS, PERSONAS_SECTION, type Persona } from '@/routes/landing/landing-data';
import { Icon, IconChip, SectionHeading, TONE_SOFT } from './shared';

export function Personas() {
  return (
    <section id="cibles" className="mx-auto w-full max-w-7xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8 lg:py-24">
      <SectionHeading eyebrow={PERSONAS_SECTION.eyebrow} title={PERSONAS_SECTION.title} subtitle={PERSONAS_SECTION.subtitle} />

      <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2">
        {PERSONAS.map((persona) => (
          <PersonaCard key={persona.title} persona={persona} />
        ))}
      </div>
    </section>
  );
}

function PersonaCard({ persona }: { persona: Persona }) {
  return (
    <div className="flex h-full flex-col rounded-token-xl border border-border bg-surface p-6 shadow-elevation-1 transition duration-200 ease-out hover:-translate-y-1 hover:border-primary/40 hover:shadow-elevation-3 motion-reduce:transition-none motion-reduce:hover:translate-y-0 sm:p-8">
      <div className="flex items-center gap-4">
        <IconChip name={persona.icon} tone={persona.tone} />
        <div>
          <h3 className="text-xl font-bold text-foreground">{persona.title}</h3>
          <p className="mt-0.5 text-sm text-muted">{persona.summary}</p>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-muted">{PERSONAS_SECTION.painsLabel}</p>
          <ul className="mt-3 space-y-2">
            {persona.pains.map((pain) => (
              <li key={pain} className="flex items-start gap-2 text-sm text-muted">
                <span aria-hidden="true" className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-muted/60" />
                <span>{pain}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className={`rounded-token-lg p-4 ${TONE_SOFT[persona.tone]}`}>
          <p className="text-xs font-semibold uppercase tracking-wide">{PERSONAS_SECTION.gainsLabel}</p>
          <ul className="mt-3 space-y-2">
            {persona.gains.map((gain) => (
              <li key={gain} className="flex items-start gap-2 text-sm text-foreground">
                <Icon name="check" className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={2.4} />
                <span>{gain}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
